import { zodResolver } from "@hookform/resolvers/zod";
import { Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  getJobById,
  handleCreateJob,
  handleUpdateJob,
} from "../../../API/recruitment/recruitmentService";
import QuillEditor from "../../../lib/quill/quill";
import BreadCrumb from "../../breadcrumb";
import Header from "../../DashboardNavigation/header";
import Sidebar from "../../DashboardNavigation/sidebar";
import { AlertModal } from "../../modal/alert-modal";
import { Button } from "../../ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Heading } from "../../ui/heading";
import { Input } from "../../ui/input";
import { Separator } from "../../ui/separator";
import {
  RecruitmentFormValues,
  recruitmentSchema,
  UpdateRecruitmentFormValues,
  updateRecruitmentSchema,
} from "./recruitment-zod-schema";

export const RecruitmentForm: React.ComponentType<any> = () => {
  const router = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState<any>(null);

  const title = isEditMode ? "Modifier l'offre d'emploi" : "Ajouter une offre d'emploi";
  const description = isEditMode ? "Modifier l'offre d'emploi" : " ";
  const action = isEditMode ? "Mise à jour" : "Ajouter";

  const form = useForm<RecruitmentFormValues | UpdateRecruitmentFormValues>({
    resolver: zodResolver(isEditMode ? updateRecruitmentSchema : recruitmentSchema),
    defaultValues: {
      title: "",
      description: "",
    },
  });

  // Charger les données si mode édition
  useEffect(() => {
    if (isEditMode && id) {
      setLoading(true);
      getJobById(Number(id)).then((data) => {
        if (data) {
          setInitialData(data);
          form.reset({
            title: data.title ?? "",
            description: data.description ?? "",
          });
        }
        setLoading(false);
      });
    }
  }, [id]);

  const onSubmit = async (values: RecruitmentFormValues | UpdateRecruitmentFormValues) => {
    setLoading(true);
    try {
      if (isEditMode && id) {
        await handleUpdateJob(Number(id), values, router);
      } else {
        await handleCreateJob(values, form.setValue, recruitmentSchema, router);
      }
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async () => {
    try {
      setLoading(true);
      router(`/admin-dashboard/recrutement`);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  const breadcrumbItems = [
    { title: "Recrutement", link: "/admin-dashboard/recrutement" },
    {
      title: isEditMode ? "Modifier" : "Ajouter",
      link: isEditMode
        ? `/admin-dashboard/recrutement/${id}`
        : "/admin-dashboard/recrutement/nouveau-offre",
    },
  ];

  return (
    <>
      <AlertModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onConfirm={onDelete}
        loading={loading}
        description="Offre d'emploi supprimée avec succès"
      />

      <Header />
      <div className="flex h-screen overflow-hidden">
        <Sidebar />
        <div className="w-full p-10">
          <BreadCrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between">
            <Heading title={title} description={description} />
            {isEditMode && (
              <Button
                disabled={loading}
                variant="destructive"
                size="sm"
                onClick={() => setOpen(true)}
              >
                <Trash className="w-4 h-4" />
              </Button>
            )}
          </div>
          <Separator />
          <Form {...form}>
            <form
              className="w-full space-y-8"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div className="gap-8 md:grid md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Titre</FormLabel>
                      <FormControl>
                        <Input
                          disabled={loading}
                          placeholder="Titre..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <QuillEditor
                        value={field.value || ""}
                        onChange={(value: string) => {
                          field.onChange(value);
                          form.trigger("description");
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                disabled={loading}
                isLoading={loading}
                className="ml-auto"
                type="submit"
              >
                {action}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
};
